import React from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Mail, Share2, Sparkles, CheckCircle, ArrowRight, Star, Shield, Zap, Users } from 'lucide-react';

const products = [
  {
    icon: FileSignature,
    title: 'eSign',
    tagline: 'Legally binding PDF signatures',
    desc: 'Upload any PDF, place signature fields, send to multiple signers, and get notified when everyone has signed. Includes audit trail and completed PDF delivery.',
    to: '/esign',
    colorClass: 'bg-blue-100 text-blue-600',
    btnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    features: ['Multi-signer workflow', 'Sequential or parallel signing', 'Email delivery', 'Audit log'],
  },
  {
    icon: Mail,
    title: 'Email Signature Builder',
    tagline: 'Animated signatures that get noticed',
    desc: 'Design professional email signatures with animations, social icons, and your brand. Export as HTML for any email client or as animated GIF.',
    to: '/email-signatures',
    colorClass: 'bg-purple-100 text-purple-600',
    btnClass: 'bg-purple-600 hover:bg-purple-700 text-white',
    features: ['7 animation types', 'Canva-like editor', 'HTML & GIF export', '10+ templates'],
  },
  {
    icon: Share2,
    title: 'Social Media Animations',
    tagline: 'Animated banners for every platform',
    desc: 'Create eye-catching animated banners sized perfectly for LinkedIn, Twitter, Instagram, and Facebook. Export as animated GIF or static image.',
    to: '/social',
    colorClass: 'bg-pink-100 text-pink-600',
    btnClass: 'bg-pink-600 hover:bg-pink-700 text-white',
    features: ['Platform presets', 'Animation library', 'GIF export', 'Brand colors'],
  },
  {
    icon: Sparkles,
    title: 'AI Image Generator',
    tagline: 'Professional images in seconds',
    desc: 'Generate stunning professional headshots, logos, email banners, and social backgrounds using DALL-E 3. Import directly into your signature builder.',
    to: '/ai-images',
    colorClass: 'bg-amber-100 text-amber-600',
    btnClass: 'bg-amber-500 hover:bg-amber-600 text-white',
    features: ['DALL-E 3 quality', 'Style presets', 'Use in builder', 'HD resolution'],
  },
];

const stats = [
  { label: 'Documents signed', value: '50K+' },
  { label: 'Active users', value: '12K+' },
  { label: 'Email signatures', value: '30K+' },
  { label: 'AI images generated', value: '100K+' },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-700 to-purple-600 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 text-sm mb-6">
            <Star className="w-4 h-4" /> Trusted by thousands of professionals
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Sign, Brand &amp; Create<br />
            <span className="text-blue-200">with Confidence</span>
          </h1>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            The all-in-one platform for e-signatures, animated email signatures,
            social media graphics, and AI-generated images.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-700 font-bold px-8 py-4 rounded-xl text-lg hover:bg-blue-50 transition-colors"
            >
              Start Free — No Credit Card
            </Link>
            <Link
              to="/pricing"
              className="border border-white/50 text-white px-8 py-4 rounded-xl text-lg hover:bg-white/10 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map(s => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-gray-900">{s.value}</div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Four Powerful Tools, One Platform</h2>
            <p className="text-xl text-gray-500">
              Everything you need to sign documents and build your professional brand online.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {products.map(p => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className={`w-12 h-12 ${p.colorClass} rounded-xl flex items-center justify-center mb-5`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{p.title}</h3>
                  <p className="text-blue-600 font-medium mb-3">{p.tagline}</p>
                  <p className="text-gray-600 mb-5">{p.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {p.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={p.to}
                    className={`inline-flex items-center gap-2 ${p.btnClass} px-5 py-2.5 rounded-lg font-medium transition-colors`}
                  >
                    Try {p.title} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <Shield className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Bank-Grade Security</h3>
              <p className="text-gray-500 text-sm">256-bit SSL encryption, SOC 2 compliant infrastructure, and complete audit trails for every signature.</p>
            </div>
            <div className="p-6">
              <Zap className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sign in Minutes</h3>
              <p className="text-gray-500 text-sm">Upload a PDF, add fields, and send to signers in under 2 minutes. No downloads or installs required.</p>
            </div>
            <div className="p-6">
              <Users className="w-10 h-10 text-purple-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Multi-Signer Support</h3>
              <p className="text-gray-500 text-sm">Send to multiple signers in any order. Everyone gets notified and receives the completed document automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600 text-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-blue-100 text-lg mb-8">
            Join thousands of professionals using EZSig every day. Free plan available.
          </p>
          <Link
            to="/register"
            className="bg-white text-blue-700 font-bold px-10 py-4 rounded-xl text-lg hover:bg-blue-50 transition-colors inline-block"
          >
            Create Free Account
          </Link>
        </div>
      </section>
    </div>
  );
}
